import * as React from "react";
import {
  buildTranscriptSecretPattern,
  findTranscriptSecrets,
  TRANSCRIPT_SECRET_MASK,
} from "@constructorfleet/utils";
import { Pill, type PillTone } from "./primitives";
import type { CallEmphasis } from "./dashboard-rows";

export { maskTranscriptSecrets } from "@constructorfleet/utils";

// ---------------------------------------------------------------------------
// CallDetailPage — full-page overlay wrapper with main content region
// ---------------------------------------------------------------------------
export function CallDetailPage({ children }: { children: React.ReactNode }) {
  return <div className="cd-page-overlay">{children}</div>;
}

export function CallDetailBody({ children }: { children: React.ReactNode }) {
  return <main className="cd-page">{children}</main>;
}

// ---------------------------------------------------------------------------
// CallDetailNav — top bar with back button and breadcrumb
// ---------------------------------------------------------------------------
export interface CallDetailNavProps {
  onBack: () => void;
  backLabel?: string;
  crumb?: React.ReactNode;
  actions?: React.ReactNode;
}
export function CallDetailNav({
  onBack,
  backLabel = "Back to dashboard",
  crumb,
  actions,
}: CallDetailNavProps) {
  return (
    <div className="cd-topnav">
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button type="button" className="back" onClick={onBack}>
          <svg viewBox="0 0 24 24">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          {backLabel}
        </button>
        {crumb && <span className="crumb">{crumb}</span>}
      </div>
      {actions && <div className="tn-right">{actions}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OutcomeHero — call result hero section
// ---------------------------------------------------------------------------
export type OutcomeVariant = "default" | "danger" | "success" | "fail";

export interface OutcomeHeroProps {
  variant?: OutcomeVariant;
  kickerLabel: React.ReactNode;
  headline: React.ReactNode;
  sub?: React.ReactNode;
  meta?: Array<{ label: string; value: React.ReactNode }>;
  actions?: React.ReactNode;
  className?: string;
  /**
   * Heading level for the outcome headline. Defaults to "h2": this hero always
   * sits beneath a page-level <h1> (a real dashboard's PageHeader, or a marketing
   * page's hero), so it should never be an <h1> itself. Visual size is preserved
   * by the .oc-headline class regardless of element.
   */
  headingLevel?: "h1" | "h2";
}
export function OutcomeHero({
  variant = "default",
  kickerLabel,
  headline,
  sub,
  meta,
  actions,
  className = "",
  headingLevel = "h2",
}: OutcomeHeroProps) {
  const Headline = headingLevel;
  const cls = ["cd-outcome", variant !== "default" && `v-${variant}`, className]
    .filter(Boolean)
    .join(" ");
  return (
    <section className={cls}>
      <div className="oc-body">
        <div>
          <div className="oc-kicker">
            <span className="d" />
            {kickerLabel}
          </div>
          <Headline className="oc-headline">{headline}</Headline>
          {sub && <p className="oc-sub">{sub}</p>}
        </div>
        {meta && meta.length > 0 && (
          <dl className="oc-meta">
            {meta.map((m) => (
              <React.Fragment key={m.label}>
                <dt>{m.label}</dt>
                <dd>{m.value}</dd>
              </React.Fragment>
            ))}
          </dl>
        )}
        {actions && <div className="oc-actions">{actions}</div>}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// NextStepBanner — action-required banner below outcome
// ---------------------------------------------------------------------------
export interface NextStepBannerProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}
export function NextStepBanner({
  icon,
  title,
  description,
  action,
  className = "",
}: NextStepBannerProps) {
  return (
    <div className={["cd-nextstep", className].filter(Boolean).join(" ")}>
      <span className="ic">
        {icon ?? (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        )}
      </span>
      <div>
        <div className="t">{title}</div>
        {description && <div className="s">{description}</div>}
      </div>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CallDetailColumns — two-column transcript + rail layout
// ---------------------------------------------------------------------------
export function CallDetailColumns({ children }: { children: React.ReactNode }) {
  return <div className="cd-cols">{children}</div>;
}

export function CallDetailRail({ children }: { children: React.ReactNode }) {
  return <aside className="cd-rail">{children}</aside>;
}

// ---------------------------------------------------------------------------
// TranscriptRow — single chat-style bubble in the transcript
// ---------------------------------------------------------------------------
export type TranscriptRowSide = "ivr" | "us";

export interface TranscriptRowProps {
  side: TranscriptRowSide;
  time: string;
  timeLabel?: string;
  who: string;
  children: React.ReactNode;
  resolution?: boolean;
}
export function TranscriptRow({
  side,
  time,
  timeLabel,
  who,
  children,
  resolution,
}: TranscriptRowProps) {
  const cls = ["cd-t-row", side, resolution && "resolution"].filter(Boolean).join(" ");
  return (
    <div className={cls}>
      <div className="cd-t-time">
        <b>{time}</b>
        {timeLabel}
      </div>
      <div>
        <div className="cd-t-who">
          {side === "ivr" && <span className="dot" />}
          {who}
          {side === "us" && <span className="dot" />}
        </div>
        <div className="cd-t-bubble">{children}</div>
      </div>
    </div>
  );
}

export function TranscriptContainer({ children }: { children: React.ReactNode }) {
  return <div className="cd-transcript">{children}</div>;
}

// ---------------------------------------------------------------------------
// DtmfInline — inline keypad indicator in transcript
// ---------------------------------------------------------------------------
export function DtmfInline({ children }: { children: React.ReactNode }) {
  return <span className="cd-dtmf-inline">{children}</span>;
}

// ---------------------------------------------------------------------------
// DtmfList + DtmfRow — summary of keys entered
// ---------------------------------------------------------------------------
export interface DtmfRowProps {
  when: string;
  label: string;
  sublabel?: string;
  keys: string;
}
export function DtmfRow({ when, label, sublabel, keys }: DtmfRowProps) {
  return (
    <div className="cd-dtmf-row">
      <span className="when">{when}</span>
      <span className="lbl">
        {label}
        {sublabel && <small>{sublabel}</small>}
      </span>
      <span className="chip">{keys}</span>
    </div>
  );
}

export function DtmfList({ children }: { children: React.ReactNode }) {
  return <div className="cd-dtmf-list">{children}</div>;
}

// ---------------------------------------------------------------------------
// RelatedCallRow — mini row in the "other calls" card
// ---------------------------------------------------------------------------
export type RelatedCallDotTone = "ok" | "scr" | "fail";

export interface RelatedCallRowProps {
  tone: RelatedCallDotTone;
  emphasis?: CallEmphasis;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  time: string;
  active?: boolean;
  onClick?: () => void;
}
export function RelatedCallRow({
  tone,
  emphasis = "none",
  title,
  subtitle,
  time,
  active,
  onClick,
}: RelatedCallRowProps) {
  const className = ["r-row", emphasis !== "none" && `r-row--${emphasis}`]
    .filter(Boolean)
    .join(" ");
  return (
    <div
      className={className}
      style={onClick ? { cursor: "pointer" } : undefined}
      onClick={onClick}
    >
      <span
        className={`dot ${tone}`}
        style={active ? { background: "var(--cf-brand)" } : undefined}
      />
      <div className="w">
        {active ? <b>{title}</b> : title}
        <small>{subtitle}</small>
      </div>
      <span className="t">{time}</span>
    </div>
  );
}

export function RelatedCallsCard({
  title = "Other calls this week",
  action,
  children,
}: {
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="card cd-related">
      <div className="card-hdr">
        <h3>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AudioPlayer — waveform player matching the mockup
// ---------------------------------------------------------------------------
export interface AudioPlayerProps {
  durationSeconds: number;
  downloadUrl?: string | null;
  downloadFilename?: string;
}

export function AudioPlayer({ durationSeconds, downloadUrl, downloadFilename }: AudioPlayerProps) {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const svgRef = React.useRef<SVGSVGElement>(null);
  const waveRef = React.useRef<HTMLDivElement>(null);
  const playheadRef = React.useRef<HTMLDivElement>(null);
  const fgClipRef = React.useRef<SVGRectElement>(null);
  const currTimeRef = React.useRef<HTMLSpanElement>(null);
  const [playing, setPlaying] = React.useState(false);
  const [speed, setSpeed] = React.useState(1);
  const [loadedDuration, setLoadedDuration] = React.useState<number>(0);
  const [error, setError] = React.useState<string | null>(null);
  const [objectUrl, setObjectUrl] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<string | null>(null);
  const pendingPlayRef = React.useRef(false);
  const rafRef = React.useRef<number>(0);
  const scrubbingRef = React.useRef(false);

  const DURATION = loadedDuration || durationSeconds || 1;
  const W = 600;
  const H = 36;
  const BARS = 120;
  const GAP = 1.5;
  const barW = (W - GAP * (BARS - 1)) / BARS;

  const fmt = React.useCallback(
    (s: number) => {
      s = Math.max(0, Math.min(DURATION, Math.floor(s)));
      const m = Math.floor(s / 60);
      const r = s % 60;
      return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
    },
    [DURATION]
  );

  const drawBars = React.useCallback(
    (amplitudes: number[]) => {
      const svg = svgRef.current;
      if (!svg) return;
      // Build the waveform via DOM APIs rather than assigning svg.innerHTML.
      // innerHTML is a Trusted Types injection sink and throws under the
      // `require-trusted-types-for 'script'` CSP directive; constructing the
      // SVG nodes explicitly is both TT-safe and avoids reparsing markup.
      const SVG_NS = "http://www.w3.org/2000/svg";
      const makeBar = (className: string, x: number, y: number, h: number) => {
        const rect = svg.ownerDocument.createElementNS(SVG_NS, "rect");
        rect.setAttribute("class", className);
        rect.setAttribute("x", x.toFixed(2));
        rect.setAttribute("y", y.toFixed(2));
        rect.setAttribute("width", barW.toFixed(2));
        rect.setAttribute("height", h.toFixed(2));
        rect.setAttribute("rx", "1");
        return rect;
      };

      const clipId = `fgClip-${Math.random().toString(36).slice(2, 8)}`;

      const clipRect = svg.ownerDocument.createElementNS(SVG_NS, "rect");
      clipRect.setAttribute("data-fg-clip", "");
      clipRect.setAttribute("x", "0");
      clipRect.setAttribute("y", "0");
      clipRect.setAttribute("width", "0");
      clipRect.setAttribute("height", String(H));

      const clipPath = svg.ownerDocument.createElementNS(SVG_NS, "clipPath");
      clipPath.setAttribute("id", clipId);
      clipPath.appendChild(clipRect);

      const defs = svg.ownerDocument.createElementNS(SVG_NS, "defs");
      defs.appendChild(clipPath);

      const bgGroup = svg.ownerDocument.createElementNS(SVG_NS, "g");
      const fgGroup = svg.ownerDocument.createElementNS(SVG_NS, "g");
      fgGroup.setAttribute("clip-path", `url(#${clipId})`);

      for (let i = 0; i < BARS; i++) {
        const a = amplitudes[i] ?? 0.04;
        const x = i * (barW + GAP);
        const h = Math.max(2, a * (H - 2));
        const y = (H - h) / 2;
        bgGroup.appendChild(makeBar("bar-bg", x, y, h));
        fgGroup.appendChild(makeBar("bar-fg", x, y, h));
      }

      svg.replaceChildren(defs, bgGroup, fgGroup);

      if (fgClipRef)
        (fgClipRef as React.MutableRefObject<SVGRectElement | null>).current = clipRect;
    },
    [barW]
  );

  // Draw placeholder bars immediately, then replace with real waveform once audio loads
  React.useEffect(() => {
    const placeholder = Array.from({ length: BARS }, () => 0.04);
    drawBars(placeholder);
  }, [drawBars]);

  // One request serves both the waveform and playback. Letting <audio src>
  // fetch the same file separately doubled the load on a rate-limited
  // endpoint, which is how playback started failing with no explanation.
  React.useEffect(() => {
    if (!downloadUrl) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    setError(null);

    void (async () => {
      let bytes: ArrayBuffer;
      try {
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          if (!cancelled) {
            setError(
              response.status === 429
                ? "Too many recording requests right now. Try again in a moment."
                : `Could not load the recording (error ${response.status}).`
            );
          }
          return;
        }
        bytes = await response.arrayBuffer();
      } catch {
        if (!cancelled) setError("Could not load the recording.");
        return;
      }
      if (cancelled) return;

      // Blob first: decodeAudioData detaches the buffer it is given.
      createdUrl = URL.createObjectURL(new Blob([bytes.slice(0)], { type: "audio/mpeg" }));
      setObjectUrl(createdUrl);

      const ac = new AudioContext();
      try {
        const decoded = await ac.decodeAudioData(bytes);
        if (cancelled) return;
        const raw = decoded.getChannelData(0);
        const bucketSize = Math.floor(raw.length / BARS);
        const amplitudes: number[] = [];
        let peak = 0;
        for (let i = 0; i < BARS; i++) {
          let sum = 0;
          const start = i * bucketSize;
          for (let j = start; j < start + bucketSize && j < raw.length; j++) {
            sum += Math.abs(raw[j]);
          }
          const avg = sum / bucketSize;
          amplitudes.push(avg);
          if (avg > peak) peak = avg;
        }
        if (peak > 0) {
          for (let i = 0; i < amplitudes.length; i++) {
            amplitudes[i] = 0.04 + (amplitudes[i] / peak) * 0.96;
          }
        }
        drawBars(amplitudes);
      } catch {
        // Undecodable audio still plays; keep the placeholder bars.
      } finally {
        void ac.close().catch(() => {});
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [downloadUrl, drawBars]);

  const render = React.useCallback(() => {
    const audio = audioRef.current;
    const cur = audio ? audio.currentTime : 0;
    const dur = audio && audio.duration && isFinite(audio.duration) ? audio.duration : DURATION;
    const pct = dur > 0 ? cur / dur : 0;
    if (playheadRef.current) playheadRef.current.style.left = `${pct * 100}%`;
    if (fgClipRef.current) fgClipRef.current.setAttribute("width", String(pct * W));
    if (currTimeRef.current) currTimeRef.current.textContent = fmt(cur);
  }, [DURATION, fmt]);

  // Handlers reach `render` through a ref so this effect does not re-subscribe
  // when the duration changes. It used to depend on [render], and the duration
  // prop arrives after mount (call events load async) — the cleanup then
  // cancelled the animation frame mid-playback and nothing restarted it, so
  // the playhead and timecode froze while audio kept playing.
  const renderRef = React.useRef(render);
  React.useEffect(() => {
    renderRef.current = render;
  }, [render]);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const startLoop = () => {
      cancelAnimationFrame(rafRef.current);
      const tick = () => {
        renderRef.current();
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };
    const stopLoop = () => {
      cancelAnimationFrame(rafRef.current);
      renderRef.current();
    };

    const onPlay = () => {
      setPlaying(true);
      startLoop();
    };
    const onPause = () => {
      setPlaying(false);
      stopLoop();
    };
    const onEnded = () => {
      setPlaying(false);
      stopLoop();
    };
    // Belt and braces: keeps the position honest if frames are throttled, as
    // they are in a background tab.
    const onTimeUpdate = () => renderRef.current();
    const onLoadedMetadata = () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        setLoadedDuration(audio.duration);
      }
    };
    const onError = () => {
      setPlaying(false);
      cancelAnimationFrame(rafRef.current);
      setError("Could not load the recording.");
    };

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("error", onError);

    // Resume the loop if this effect re-ran while audio was already playing.
    if (!audio.paused) {
      setPlaying(true);
      startLoop();
    }

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("error", onError);
      cancelAnimationFrame(rafRef.current);
    };
  }, [objectUrl]);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.playbackRate = speed;
  }, [speed]);

  React.useEffect(() => {
    if (!objectUrl || !pendingPlayRef.current) return;
    pendingPlayRef.current = false;
    setStatus(null);
    audioRef.current?.play().catch(() => {
      setError("Could not play the recording.");
    });
  }, [objectUrl]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      setError(null);
      // Bytes are still downloading. Remember the intent and start as soon as
      // they land, rather than making the button appear dead.
      if (downloadUrl && !objectUrl) {
        pendingPlayRef.current = true;
        setStatus("Preparing the recording…");
        return;
      }
      // A swallowed rejection here was the whole symptom: the button did
      // nothing and said nothing.
      audio.play().catch((cause: unknown) => {
        setPlaying(false);
        setStatus(null);
        const name = cause instanceof Error ? cause.name : "";
        setError(
          name === "NotAllowedError"
            ? "Could not play: the browser blocked playback. Press play again."
            : "Could not play the recording."
        );
      });
    } else {
      audio.pause();
    }
  };

  const seekFromEvent = React.useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      const el = waveRef.current;
      const audio = audioRef.current;
      if (!el || !audio) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      const dur = audio.duration && isFinite(audio.duration) ? audio.duration : DURATION;
      audio.currentTime = pct * dur;
      render();
    },
    [DURATION, render]
  );

  React.useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (scrubbingRef.current) seekFromEvent(e);
    };
    const handleUp = () => {
      scrubbingRef.current = false;
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [seekFromEvent]);

  const speeds = [1, 1.25, 1.5, 2, 0.75];
  const handleSpeed = () => {
    const idx = speeds.indexOf(speed);
    const next = speeds[(idx + 1) % speeds.length];
    setSpeed(next);
  };

  const handleSkipBack = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime - 10);
    render();
  };

  return (
    <div className="cd-audio">
      {downloadUrl && (
        <audio ref={audioRef} {...(objectUrl ? { src: objectUrl } : {})} preload="auto" />
      )}
      <button
        className={`play${playing ? " playing" : ""}`}
        onClick={handlePlayPause}
        aria-label={playing ? "Pause" : "Play"}
        type="button"
      >
        <svg className="play-icon" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7L8 5z" />
        </svg>
        <svg className="pause-icon" viewBox="0 0 24 24">
          <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
        </svg>
      </button>
      <div className="cd-audio-mid">
        <div
          className="wave"
          ref={waveRef}
          onMouseDown={(e) => {
            scrubbingRef.current = true;
            seekFromEvent(e);
          }}
        >
          <svg ref={svgRef} preserveAspectRatio="none" viewBox={`0 0 ${W} ${H}`} />
          <div className="playhead" ref={playheadRef} style={{ left: "0%" }} />
        </div>
        <div className="tcs">
          <span>
            <span className="curr" ref={currTimeRef}>
              00:00
            </span>{" "}
            / {fmt(DURATION)}
          </span>
          <span>Call recording · mono · 16 kHz</span>
        </div>
        {error ? (
          <p className="cd-audio-error" role="status">
            {error}
          </p>
        ) : status ? (
          <p className="cd-audio-status" role="status">
            {status}
          </p>
        ) : null}
      </div>
      <div className="cd-audio-right">
        <button
          className="btn btn-ghost btn-sm speed-btn"
          onClick={handleSpeed}
          type="button"
          title="Playback speed"
        >
          {speed}×
        </button>
        <button
          className="icon-btn"
          onClick={handleSkipBack}
          type="button"
          title="Back 10s"
          aria-label="Back 10 seconds"
        >
          <svg viewBox="0 0 24 24">
            <path d="M12 5V1L6 6l6 5V7a6 6 0 11-6 6" />
          </svg>
        </button>
        {downloadUrl && (
          <a
            className="icon-btn"
            href={downloadUrl}
            download={downloadFilename}
            title="Download recording"
            aria-label="Download recording"
          >
            <svg viewBox="0 0 24 24">
              <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
            </svg>
          </a>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CallDetailTranscript — renders flat transcript text with design-system
// chat bubble styling, parsing [keys:...] markers into DtmfInline tags
// ---------------------------------------------------------------------------
export interface CallDetailTranscriptProps {
  text: string | null | undefined;
  ivrLabel?: string;
  usLabel?: string;
  /** Draws the eye to the closing bubble, which is where the result is spoken. */
  highlightFinal?: boolean;
  /** Hides keyed identifiers (patient ID, SSN digits) until the reader clicks one. */
  maskSecrets?: boolean;
}

// Keyed identifiers stay hidden on screen so the page is safe to open in public.
// One click reveals every one of them for this view; the clinic dispute case needs
// the whole transcript legible, not one chip at a time.
function SecretDigits({
  value,
  revealed,
  onToggle,
  className = "",
}: {
  value: string;
  revealed: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={["cd-secret", revealed ? "is-revealed" : "", className].filter(Boolean).join(" ")}
      onClick={onToggle}
      aria-label={revealed ? "Hide digits" : "Reveal digits"}
      aria-pressed={revealed}
      title={revealed ? "Click to hide" : "Click to reveal"}
    >
      {revealed ? value : value.replace(/\d+/g, TRANSCRIPT_SECRET_MASK)}
    </button>
  );
}

function renderProseWithSecrets(
  content: string,
  pattern: RegExp | null,
  revealed: boolean,
  onToggle: () => void
): React.ReactNode {
  if (!pattern) return content;
  const parts: React.ReactNode[] = [];
  let last = 0;
  pattern.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(content)) !== null) {
    if (m.index > last) parts.push(content.slice(last, m.index));
    parts.push(<SecretDigits key={m.index} value={m[0]} revealed={revealed} onToggle={onToggle} />);
    last = m.index + m[0].length;
  }
  if (parts.length === 0) return content;
  if (last < content.length) parts.push(content.slice(last));
  return parts;
}

export function CallDetailTranscript({
  text,
  ivrLabel = "IVR",
  usLabel = "ConstructorFleet",
  highlightFinal = false,
  maskSecrets = true,
}: CallDetailTranscriptProps) {
  const [revealed, setRevealed] = React.useState(false);
  const toggleRevealed = React.useCallback(() => setRevealed((value) => !value), []);
  const secretPattern = React.useMemo(
    () => (maskSecrets ? buildTranscriptSecretPattern(findTranscriptSecrets(text)) : null),
    [text, maskSecrets]
  );

  if (!text) {
    return (
      <div className="cd-transcript">
        <p style={{ margin: 0, padding: "1rem", color: "var(--cf-text-muted)", fontSize: 13 }}>
          No transcript available.
        </p>
      </div>
    );
  }

  const KEYS_RE = /\[keys:([^\]]+)\]/g;
  const segments: Array<{ type: "text"; content: string } | { type: "keys"; digits: string }> = [];
  let last = 0;
  let m: RegExpExecArray | null;
  KEYS_RE.lastIndex = 0;
  while ((m = KEYS_RE.exec(text)) !== null) {
    if (m.index > last) segments.push({ type: "text", content: text.slice(last, m.index) });
    segments.push({ type: "keys", digits: m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ type: "text", content: text.slice(last) });

  const rows: React.ReactNode[] = [];
  let idx = 0;
  for (const seg of segments) {
    if (seg.type === "keys") {
      rows.push(
        <div key={idx++} className="cd-t-row us">
          <div className="cd-t-time">
            <b>DTMF</b>
          </div>
          <div>
            <div className="cd-t-who">
              {usLabel} <span className="dot" />
            </div>
            <div className="cd-t-bubble">
              Entered{" "}
              {secretPattern && seg.digits.search(secretPattern) !== -1 ? (
                <SecretDigits
                  className="cd-dtmf-inline"
                  value={seg.digits}
                  revealed={revealed}
                  onToggle={toggleRevealed}
                />
              ) : (
                <span className="cd-dtmf-inline">{seg.digits}</span>
              )}
            </div>
          </div>
        </div>
      );
    } else {
      const trimmed = seg.content.trim();
      if (!trimmed) continue;
      rows.push(
        <div key={idx++} className="cd-t-row ivr">
          <div className="cd-t-time">
            <b>—</b>
          </div>
          <div>
            <div className="cd-t-who">
              <span className="dot" />
              {ivrLabel}
            </div>
            <div className="cd-t-bubble">
              {renderProseWithSecrets(trimmed, secretPattern, revealed, toggleRevealed)}
            </div>
          </div>
        </div>
      );
    }
  }

  if (rows.length === 0) {
    rows.push(
      <div key="full" className="cd-t-row ivr">
        <div className="cd-t-time">
          <b>—</b>
        </div>
        <div>
          <div className="cd-t-who">
            <span className="dot" />
            {ivrLabel}
          </div>
          <div className="cd-t-bubble">{text}</div>
        </div>
      </div>
    );
  }

  if (highlightFinal && rows.length > 0) {
    const last = rows[rows.length - 1] as React.ReactElement<{ className?: string }>;
    rows[rows.length - 1] = React.cloneElement(last, {
      className: [last.props.className, "cd-t-row--final"].filter(Boolean).join(" "),
    });
  }

  return <div className="cd-transcript">{rows}</div>;
}

// ---------------------------------------------------------------------------
// ResultPill — outcome pills for the meta section
// ---------------------------------------------------------------------------
export function ResultPill({ tone, children }: { tone: PillTone; children: React.ReactNode }) {
  return (
    <Pill tone={tone}>
      <span
        className="d"
        style={{
          width: 5,
          height: 5,
          borderRadius: 999,
          background: "currentColor",
          display: "inline-block",
        }}
      />
      {children}
    </Pill>
  );
}
